import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, effect, inject, signal, untracked } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { ToastrService } from 'ngx-toastr';

import { ApiService, BackendType } from './api.service';
import { LanguageService } from './lang.service';
import {
  Circle,
  IUpdateCircle,
  IUpdateUser,
  IUserState,
  User,
  UserPhoneNumbers,
  UserSearch,
} from '../models/api_models';
import { OnSiteDestinationId, OnSiteDestinationType } from '../utils/constants';

@Injectable({
  providedIn: 'root',
})
export class CircleService {
  private toastr = inject(ToastrService);
  private router = inject(Router);
  apiService = inject(ApiService);
  translocoService = inject(TranslocoService);
  langService = inject(LanguageService);

  circles = signal<Circle[] | undefined>(undefined);
  circle = signal<Circle | undefined>(undefined);
  circlesLoading = signal<boolean>(false);
  addingCircles = signal<boolean>(false);
  loadingCircles = signal<boolean>(false);
  loadingCircle = signal<boolean>(false);
  loadingCircleSummary = signal<boolean>(false);
  updatingCircle = signal<boolean>(false);
  updatedCircle = signal<Circle | undefined>(undefined);
  searchingUser = signal<boolean>(false);
  searchedUsers = signal<User[] | undefined>(undefined);
  updatingUserState = signal<number | undefined>(undefined);
  userDeleted = signal<IUpdateUser[] | undefined>(undefined);
  circleSummary = signal<any[] | undefined>(undefined);
  loadingUserInfo = signal<boolean>(false);
  userInfo = signal<any>(undefined);
  loadingUserNumbers = signal<boolean>(false);
  userNumbers = signal<UserPhoneNumbers | undefined>(undefined);
  languageChange = signal<number>(0);
  currentRoute = signal<string | undefined>(undefined);

  _languageChange = effect(() => {
    // this languageChange effect is needed, because we add manually a subgroupEntry that has translated texts
    const _activeLanguage = this.langService.getLangSignal()();
    const circles = this.circles();
    const circle = this.circle();
    untracked(() => {
      let shouldIncrementLanguageChange = false;

      if (circles) {
        const translatedCircles = this.addMetaInfoToUsers(circles);
        this.circles.set(translatedCircles);
        shouldIncrementLanguageChange = true;
      }
      if (circle) {
        const translatedCircle = this.addMetaInfoToUsers([circle])[0];
        this.circle.set(translatedCircle);
        shouldIncrementLanguageChange = true;
      }

      if (shouldIncrementLanguageChange) {
        this.languageChange.set(this.languageChange() + 1);
      }
    });
  });

  _init = effect(() => {
    this.router.events.subscribe({
      next: event => {
        if (
          event instanceof NavigationEnd &&
          this.currentRoute() !== event.url &&
          (this.circle() || this.circles())
        ) {
          this.currentRoute.set(event.url);
          this.circle.set(undefined);
          this.circles.set(undefined);
        }
      },
      error: (error: HttpErrorResponse) => {
        this.handleError(error, true);
      },
      complete: () => {
        this.loadingCircles.set(false);
      },
    });
  });

  private handleError(error: HttpErrorResponse, redirectUnauthorized: boolean) {
    if (error.status === 401 || error.status === 403) {
      if (redirectUnauthorized) {
        this.router.navigate(['unauthorized']);
      }
    } else if (error.status === 404) {
      this.router.navigate(['404']);
    } else if (error.error?.detail) {
      this.toastr.error(error.error?.detail);
    } else {
      this.toastr.error(this.translocoService.translate('switchboard.errors.genericApiError'));
    }
  }

  // set onCall and isOnSite property depending on the information on circle level for isOnSite and the information on organization level for onCall
  private addMetaInfoToUsers(circles: Circle[]) {
    for (const circle of circles) {
      this.addMetaInfoToUsersCircle(circle);
    }
    return circles;
  }

  private addMetaInfoToUsersCircle(circle: Circle) {
    circle.onSite = {
      id: circle.id,
      name: 'officePhone',
      users: [
        {
          id: 0,
          first_name: this.translocoService.translate('switchboard.officePhone'),
          last_name: '',
          role: this.translocoService.translate('switchboard.standardPhone'),
          mobile: circle.mainNumber,
          onCall: false,
          isOnSite: circle.selectedNumber === OnSiteDestinationType.OFFICE_PHONE,
          activeOnCall: true,
          activeOnSite: true,
        },
      ],
      aad_groups: [],
    };

    if (circle.answeringMachineNumber) {
      circle.answeringMachine = {
        id: OnSiteDestinationId.ANSWERING_MACHINE,
        name: 'answeringMachine',
        users: [
          {
            id: OnSiteDestinationId.ANSWERING_MACHINE,
            first_name: this.translocoService.translate('switchboard.answeringMachine'),
            last_name: '',
            role: this.translocoService.translate('switchboard.answeringMachineSubtitle'),
            mobile: circle.answeringMachineNumber,
            onCall: false,
            isOnSite: circle.selectedNumber === OnSiteDestinationType.ANSWERING_MACHINE,
            activeOnCall: true,
            activeOnSite: true,
          },
        ],
        aad_groups: [],
      };
    }

    const isOnSiteUserId =
      circle.userMainNumber && circle.selectedNumber === OnSiteDestinationType.USER
        ? circle.userMainNumber.id
        : undefined;
    const organizations = circle.organizations;

    if (organizations) {
      for (const organization of organizations) {
        const onCallUserId = organization.userOnCall?.id;
        organization.subgroups.forEach(subgroup => {
          if (subgroup.users) {
            subgroup.users.forEach(user => {
              user.onCall = user.id === onCallUserId;
              user.isOnSite = user.id === isOnSiteUserId;
            });

            const leader = subgroup.users
              .filter(
                user => user.automaticallyAdded && user.role?.toLowerCase().includes('leiter'),
              )
              .sort(
                (a, b) =>
                  a.first_name?.localeCompare(b.first_name) ||
                  a.last_name?.localeCompare(b.last_name),
              );
            const automaticallyAdded = subgroup.users
              .filter(
                user => user.automaticallyAdded && !user.role?.toLowerCase().includes('leiter'),
              )
              .sort(
                (a, b) =>
                  a.first_name?.localeCompare(b.first_name) ||
                  a.last_name?.localeCompare(b.last_name),
              );
            const notAutomaticallyAdded = subgroup.users
              .filter(user => !user.automaticallyAdded)
              .sort(
                (a, b) =>
                  a.first_name?.localeCompare(b.first_name) ||
                  a.last_name?.localeCompare(b.last_name),
              );
            subgroup.users = [...leader, ...automaticallyAdded, ...notAutomaticallyAdded];
          }
        });
      }
    }

    return circle;
  }

  updateCircle(circleId: number, circle: IUpdateCircle) {
    this.updatingCircle.set(true);
    this.apiService
      .put<Circle>(
        `api/v1/circle/update/${circleId}`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
          body: circle,
        },
        BackendType.SB,
      )
      .subscribe({
        next: circleResponse => {
          this.updatedCircle.set(circleResponse);
          const currentCircles = this.circles();
          const circle2UpdateIndex =
            currentCircles?.findIndex(x => x.id === circleResponse.id) ?? -1;
          if (currentCircles && circle2UpdateIndex > -1) {
            currentCircles[circle2UpdateIndex] = this.addMetaInfoToUsersCircle(circleResponse);
            this.circles.set([]); // this is needed so effects will trigger change
            this.circles.set(currentCircles);
          }
          if (circle.remove_users && circle.remove_users?.length > 0) {
            this.userDeleted.set(circle.remove_users);
          }
          this.getCircle(circleId);
        },
        error: (error: HttpErrorResponse) => {
          this.handleError(error, true);
        },
        complete: () => {
          this.updatingCircle.set(false);
        },
      });
  }

  getCircles() {
    this.loadingCircles.set(true);
    this.apiService
      .get<Circle[]>(
        `api/v1/circle`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
        BackendType.SB,
      )
      .subscribe({
        next: circles => {
          this.circles.set(this.addMetaInfoToUsers(circles));
        },
        error: (error: HttpErrorResponse) => {
          this.handleError(error, true);
        },
        complete: () => {
          this.loadingCircles.set(false);
        },
      });
  }

  getCircle(id: number) {
    this.loadingCircle.set(true);
    this.apiService
      .get<Circle>(
        `api/v1/circle/${id}`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
        BackendType.SB,
      )
      .subscribe({
        next: circle => {
          this.circle.set(this.addMetaInfoToUsers([circle])[0]);
        },
        error: (error: HttpErrorResponse) => {
          this.handleError(error, true);
        },
        complete: () => {
          this.loadingCircle.set(false);
        },
      });
  }

  getCircleSummary() {
    this.loadingCircleSummary.set(true);
    this.apiService
      .get<any[]>(
        `/api/v1/circle/summary`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
        BackendType.SB,
      )
      .subscribe({
        next: circles => {
          this.circleSummary.set(circles);
        },
        error: (error: HttpErrorResponse) => {
          this.handleError(error, true);
        },
        complete: () => {
          this.loadingCircleSummary.set(false);
        },
      });
  }

  searchUser(searchTerm: string) {
    this.searchingUser.set(true);
    const searchTermsEncoded = searchTerm.split(' ').map(term => encodeURIComponent(term));
    const searchEncoded = searchTermsEncoded.join('&searchTerms=');
    this.apiService
      .get<UserSearch>(
        `api/v1/user/?searchTerms=${searchEncoded}&currentPage=1&pageSize=100`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
        BackendType.SB,
      )
      .subscribe({
        next: userSearch => {
          this.searchedUsers.set(userSearch.data);
        },
        error: (error: HttpErrorResponse) => {
          this.handleError(error, true);
        },
        complete: () => {
          this.searchingUser.set(false);
        },
      });
  }

  setUserState(userState: IUserState[], circleId: number) {
    this.apiService
      .post<any>(
        `/api/v1/subgroup/set_users_state`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
          body: userState,
        },
        BackendType.SB,
      )
      .subscribe({
        next: () => {
          this.getCircle(circleId);
        },
        error: (error: HttpErrorResponse) => {
          this.handleError(error, true);
        },
        complete: () => {
          this.updatingUserState.set(Date.now());
        },
      });
  }

  getUserInfo() {
    this.loadingUserInfo.set(true);
    this.apiService
      .get<any[]>(
        `/api/v1/user/auth-user`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
        BackendType.SB,
      )
      .subscribe({
        next: info => {
          this.userInfo.set(info);
        },
        error: (error: HttpErrorResponse) => {
          this.userInfo.set(null);
          this.handleError(error, false);
        },
        complete: () => {
          this.loadingUserInfo.set(false);
        },
      });
  }

  getUserNumbers() {
    this.loadingUserNumbers.set(true);
    this.apiService
      .get<UserPhoneNumbers>(
        `/api/v1/user/get-numbers`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
        BackendType.SB,
      )
      .subscribe({
        next: numbers => {
          this.userNumbers.set(numbers);
          this.loadingUserNumbers.set(false);
        },
        error: (_error: HttpErrorResponse) => {
          this.userNumbers.set(undefined);
          this.loadingUserNumbers.set(false);
        },
        complete: () => {
          this.loadingUserNumbers.set(false);
        },
      });
  }

  updateUserNumbers(numbers: UserPhoneNumbers) {
    this.loadingUserNumbers.set(true);
    this.apiService
      .post<UserPhoneNumbers>(
        `/api/v1/user/update-numbers`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
          body: numbers,
        },
        BackendType.SB,
      )
      .subscribe({
        next: _updatedNumbers => {
          this.getCircle(this.circle()?.id as number);
          this.toastr.success(
            this.translocoService.translate('switchboard.userPhoneUpdateSuccess'),
          );
          this.loadingUserNumbers.set(false);
        },
        error: (error: HttpErrorResponse) => {
          this.handleError(error, false);
          this.loadingUserNumbers.set(false);
        },
        complete: () => {
          this.loadingUserNumbers.set(false);
        },
      });
  }
}
